import CardCarousel from 'LuxKit/Carousel/CardCarousel'
import Group from 'components/utils/Group'
import PaginationDots from 'LuxKit/PaginationDots/PaginationDots'
import { useCallback, useMemo, useRef, useState } from 'react'
import styled from 'styled-components'
import debounce from 'debounce-promise'
import WeddingMessage from './WeddingMessage'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import { getLogicalScrollLeft } from 'LuxKit/Carousel/utils'
import { mediaQueryUp } from 'components/utils/breakpoint'

const MessagesCarousel = styled(CardCarousel)<{ $pageColumnCount: number }>`
  width: 100%;

  ${mediaQueryUp.desktop} {
    .paged {
      grid-template-columns: repeat(${props => props.$pageColumnCount}, calc(100% / var(--carousel-items)));
    }

    .paged > * {
      width: calc(100% - var(--carousel-gap));
    }
  }
`

const MessageCarouselItem = styled.div<{ $column: number, $row: number }>`
  ${mediaQueryUp.desktop} {
    grid-column: ${props => props.$column};
    grid-row: ${props => props.$row};

    > * {
      width: 100%;
    }
  }
`

interface Props {
  messages: Array<{
    id: string
    authorName: string
    content: string
    date: string
  }>
}

function WeddingMessagesList(props: Props) {
  const { messages } = props

  const carouselRef = useRef<HTMLDivElement>(null)

  const [activeIndex, setActiveIndex] = useState(0)
  const pages = useMemo(() => {
    return Math.ceil(messages.length / 8)
  }, [messages])
  const pageColumnCount = pages * 4

  const debouncedOnScroll = useMemo(() => {
    return debounce(() => {
      if (carouselRef.current) {
        const newIndex = Math.round(getLogicalScrollLeft(carouselRef.current) / (carouselRef.current.scrollWidth / pages))
        setActiveIndex(Math.max(0, Math.min(newIndex, pages - 1)))
      }
    }, 50)
  }, [pages, carouselRef])

  const onScroll = useCallback(() => {
    debouncedOnScroll()
  }, [debouncedOnScroll])

  return (
    <>
      <MessagesCarousel
        $pageColumnCount={pageColumnCount}
        pageSize={4}
        desktopRows={2}
        desktopRowGap={28}
        gap={20}
        ref={carouselRef}
        onScroll={onScroll}
      >
        {messages.map((message, index) => (
          <MessageCarouselItem
            key={message.id}
            $column={(Math.floor(index / 8) * 4) + (index % 4) + 1}
            $row={Math.floor((index % 8) / 4) + 1}
          >
            <WeddingMessage message={message} />
          </MessageCarouselItem>
        ))}
      </MessagesCarousel>
      <CSSBreakpoint min="desktop">
        <Group direction="horizontal" gap={0} horizontalAlign="center">
          <PaginationDots
            count={pages}
            activeIndex={activeIndex}
          />
        </Group>
      </CSSBreakpoint>
    </>
  )
}

export default WeddingMessagesList
